import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ClinicManagerService } from '../../networking/services/clinic-manager.service';
import { AuthService } from '../../helper/services/auth.service';
import { CONSTANTS } from '../../helper/constants';
import { ERRORCODES } from '../../helper/error-codes';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { TimeoutError } from 'rxjs';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressBarModule,
    MatCheckboxModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  currentView: 'login' | 'reset-request' | 'reset-info' | 'register' = 'login'
  loginData: FormGroup
  resetRequestForm: FormGroup
  registerForm: FormGroup
  hide = true
  errorMessage: string | null = null
  isLoading = false

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private clinicManagerService: ClinicManagerService,
    private authService: AuthService
  ) {
    this.loginData = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    })
    this.resetRequestForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    })
    this.registerForm = this.fb.group({
      clinicName: ['', [Validators.required]],
      contactName: ['', [Validators.required]],
      contactEmail: ['', [Validators.required, Validators.email]],
      contactPhone: [''],
      comment: [''],
      privacyConsent: [false, [Validators.requiredTrue]]
    })
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/portal'])
    }
  }

  async submitLogin(): Promise<void> {
    if (this.loginData.invalid) return

    this.isLoading = true
    this.errorMessage = null

    try {
      const response = await this.clinicManagerService.login(this.loginData.value)
      this.authService.setCurrentManager(response)
      this.router.navigate(['/portal'])
    } catch (error: any) {
      if (!environment.production) {
        console.error('Login error:', error)
        console.error('Error type:', error?.constructor?.name)
        console.error('Error body:', error?.body)
        console.error('Error status:', error?.status)
        console.error('Error message:', error?.message)
      }

      // Duck-Typing zusätzlich zu instanceof-Checks: bei Modul-Duplizierung
      // im Bundle kann instanceof unzuverlässig werden — über die status-
      // bzw. body-Property erkennen wir das Error-Format trotzdem.
      const hasHttpStatus = typeof error?.status === 'number'
      const hasErrorBody = typeof error?.body === 'string' && ERRORCODES.isErrorCode(error.body)

      if (error instanceof TimeoutError) {
        this.errorMessage = CONSTANTS.connectionError
      } else if (error instanceof HttpResponse || hasErrorBody) {
        // Backend hat HTTP 200 mit Error-Code im Body zurückgegeben
        switch (error.body) {
          case ERRORCODES.RESPONSE_FAILED_LOGIN.error_code:
            this.errorMessage = CONSTANTS.loginError
            break
          case ERRORCODES.RESPONSE_FORBIDDEN.error_code:
            this.errorMessage = CONSTANTS.forbiddenError
            break
          default:
            this.errorMessage = CONSTANTS.unknownError
            break
        }
      } else if (error instanceof HttpErrorResponse || hasHttpStatus) {
        // Backend hat HTTP 4xx/5xx zurückgegeben
        switch (error.status) {
          case 0:
            this.errorMessage = CONSTANTS.connectionError
            break
          case 401:
            this.errorMessage = CONSTANTS.loginError
            break
          case 403:
            this.errorMessage = CONSTANTS.forbiddenError
            break
          case 405:
          case 502:
          case 503:
          case 504:
            this.errorMessage = CONSTANTS.serviceUnavailableError
            break
          default:
            this.errorMessage = environment.production
              ? CONSTANTS.unknownError
              : `${CONSTANTS.unknownError} (HTTP ${error.status}: ${error.statusText ?? ''})`
            break
        }
      } else {
        this.errorMessage = CONSTANTS.unknownError
      }
    } finally {
      this.isLoading = false
    }
  }

  async submitResetRequest(): Promise<void> {
    if (this.resetRequestForm.invalid) return

    this.isLoading = true
    this.errorMessage = null

    try {
      await this.clinicManagerService.resetPassword(this.resetRequestForm.value.email)
    } catch {
      // Fehler bewusst ignoriert — Nachricht wird immer angezeigt
    } finally {
      this.isLoading = false
      this.currentView = 'reset-info'
    }
  }

  showResetRequest(): void {
    this.currentView = 'reset-request'
    this.errorMessage = null
  }

  showLogin(): void {
    this.currentView = 'login'
    this.errorMessage = null
  }

  showRegister(): void {
    this.currentView = 'register'
    this.errorMessage = null
  }

  submitRegister(): void {
    if (this.registerForm.invalid) return

    const v = this.registerForm.value
    const subject = encodeURIComponent(`Klinikportal Registrierung: ${v.clinicName}`)
    const body = encodeURIComponent(
      `Name der Klinik/Institution: ${v.clinicName}\n` +
      `Name Ansprechperson: ${v.contactName}\n` +
      `E-Mail-Adresse: ${v.contactEmail}\n` +
      `Telefonnummer: ${v.contactPhone || '-'}\n` +
      `Kommentar: ${v.comment || '-'}\n` +
      `\nDatenschutzeinwilligung erteilt: Ja`
    )
    window.location.href = `mailto:hallo@neodiary.app?subject=${subject}&body=${body}`
  }

}
